import gql from "graphql-tag";

// Queries

export const PATIENT_PROFILE_FIELDS = gql`
  fragment PatientProfileFields on PatientProfileType {
    id
    pk
    birthControl
    postMenopausal
    hrt
    height
    weight
    dateOfBirth
    gender
    isComplete
    user {
      id
      firstName
      lastName
      email
    }
    goals {
      id
    }
    dietPreferences {
      id
    }
    marketingConsent
    testProductFavouriteIds
    wards {
      id
      user {
        id
        firstName
        lastName
      }
    }
  }
`;

export const PATIENT_PROFILE_QUERY = gql`
  query PatientProfileQuery {
    patientProfile {
      ...PatientProfileFields
    }
  }
  ${PATIENT_PROFILE_FIELDS}
`;

export const PRACTITIONER_TYPE_FIELDS = gql`
  fragment PractitionerTypeFields on PractitionerTypeType {
    id
    role
    qualificationTypes
  }
`;

export const PRACTITIONER_TYPES_QUERY = gql`
  query PractitionerTypesQuery {
    practitionerTypes {
      ...PractitionerTypeFields
    }
  }
  ${PRACTITIONER_TYPE_FIELDS}
`;

export const PROFESSIONAL_BODY_FIELDS = gql`
  fragment ProfessionalBodyFields on ProfessionalBodyType {
    id
    name
    requiresRegistrationNumber
    registrationNumberLabel
  }
`;

export const SPECIALISMS_AND_PRACTITIONER_TYPES_QUERY = gql`
  query SpecialismsAndPractitionerTypesQuery {
    practitionerTypes {
      ...PractitionerTypeFields
    }
    professionalBodyOptions: professionalBodies {
      ...ProfessionalBodyFields
    }
    specialisms: healthAreas {
      id
      name
    }
  }
  ${PRACTITIONER_TYPE_FIELDS}
  ${PROFESSIONAL_BODY_FIELDS}
`;

export const EXPERT_TESTIMONIAL_FIELDS = gql`
  fragment ExpertTestimonialFields on TestimonialType {
    id
    detail
    name
    order
  }
`;

export const EXPERT_FIELDS = gql`
  fragment ExpertFields on ExpertType {
    id
    fullName
    firstName
    lastName
    businessLocationCity
    practitionerType {
      id
      role
      qualificationTypes
    }
    organisationNames
    professionalBodies
    documents {
      id
      type
      qualificationType
    }
    public
    avatar
    avatarUrl
    resizedAvatarUrl
    bio
    consultationType
    feeLevel
    freeConsultation
    supportedTests {
      id
      name
      paymentIsoCode
      organisation
    }
    specialisms {
      id
      name
      description
    }
    testimonials {
      ...ExpertTestimonialFields
    }
    languages {
      id
      name
    }
  }
  ${EXPERT_TESTIMONIAL_FIELDS}
`;

export const PRACTITIONER_PROFILE_FIELDS = gql`
  fragment PractitionerProfileFields on PractitionerProfileType {
    id
    tier
    approved
    user {
      id
      firstName
      lastName
    }
    jobRole
    phoneNumber
    instagram
    linkedin
    youtube
    websiteBlog
    isApprovedToBeExpert
    currentPractitionerLevel {
      level
      approved
      patientOrderPermission
    }
    practitionerType {
      id
      role
    }
    professionalBodies
    documents {
      id
      fileUrl
      fileName
      fileKey
    }
    expert {
      ...ExpertFields
    }
    businessLocationCity
    specialismsM2m {
      id
      name
    }
  }
  ${EXPERT_FIELDS}
`;

export const PRACTITIONER_PROFILE_QUERY = gql`
  query PractitionerProfileQuery {
    practitionerProfile {
      ...PractitionerProfileFields
    }
  }
  ${PRACTITIONER_PROFILE_FIELDS}
`;

export const GENERIC_USER_PROFILE_QUERY = gql`
  query GenericUserProfileQuery {
    user {
      id
      email
      dateJoined
      fullName
      firstName
      lastName
      entitlements {
        id
        key
        granted
      }
      patientProfile {
        ...PatientProfileFields
      }
      practitionerProfile {
        ...PractitionerProfileFields
        showReportsTab
      }
    }
  }
  ${PATIENT_PROFILE_FIELDS}
  ${PRACTITIONER_PROFILE_FIELDS}
`;

export const RESULTS_ACCESS_REQUEST_QUERY = gql`
  query ResultAccessRequestQuery($id: String!) {
    resultsAccessLink(id: $id) {
      id
      requesterName
      requesterIsOrganisation
      patientProfile {
        id
        isWard
        guardian {
          id
        }
        user {
          id
          fullName
        }
      }
    }
  }
`;

export const USER_PURCHASE_COMMISSION_FIELDS = gql`
  fragment UserPurchaseCommissionFields on CommissionType {
    orderDescription
    discount
    commission
    orderDate
    orderId
  }
`;

export const PAYOUT_FIELDS = gql`
  fragment PayoutFields on PayoutType {
    id
    created
    amount
    status
  }
`;

export const PAYOUTS_QUERY = gql`
  query PayoutsQuery {
    payouts {
      ...PayoutFields
    }
  }
  ${PAYOUT_FIELDS}
`;

// Mutations

export const SIGNUP_MUTATION = gql`
  mutation SignupMutation($input: SignupMutationInput!) {
    signupMutation(input: $input) {
      user {
        id
        email
      }
      errors {
        field
        messages
      }
    }
  }
`;

export const ACCEPT_PATIENT_INVITE_MUTATION = gql`
  mutation AcceptPatientInviteMutation($input: AcceptPatientInviteMutationInput!) {
    acceptPatientInviteMutation(input: $input) {
      errors {
        field
        messages
      }
    }
  }
`;

export const ACTIVATE_ACCOUNT_MUTATION = gql`
  mutation ActivateAccountMutation($input: ActivateAccountMutationInput!) {
    activateAccountMutation(input: $input) {
      errors {
        field
        messages
      }
    }
  }
`;

export const LOG_IN_MUTATION = gql`
  mutation LogInMutation($input: LogInMutationInput!) {
    logInMutation(input: $input) {
      user {
        id
        email
        dateJoined
        fullName
        patientProfile {
          ...PatientProfileFields
        }
        practitionerProfile {
          ...PractitionerProfileFields
        }
      }
      errors {
        field
        messages
      }
      csrfToken
      hubspotToken
    }
  }
  ${PRACTITIONER_PROFILE_FIELDS}
  ${PATIENT_PROFILE_FIELDS}
`;

export const LOG_OUT_MUTATION = gql`
  mutation LogOutMutation {
    logOutMutation {
      result
    }
  }
`;

export const REQUEST_PASSWORD_RESET_MUTATION = gql`
  mutation RequestPasswordResetMutation($input: RequestPasswordResetMutationInput!) {
    requestPasswordResetMutation(input: $input) {
      errors {
        field
        messages
      }
    }
  }
`;

export const RESEND_VERIFICATION_EMAIL = gql`
  mutation ResendVerificationEmailMutation($input: ResendVerificationEmailMutationInput!) {
    resendVerificationEmailMutation(input: $input) {
      errors {
        field
        messages
      }
    }
  }
`;

export const RESET_PASSWORD_MUTATION = gql`
  mutation ResetPasswordMutation($input: ResetPasswordMutationInput!) {
    resetPasswordMutation(input: $input) {
      errors {
        field
        messages
      }
    }
  }
`;

export const ACCEPT_PROFESSIONAL_LINK_INVITATION_MUTATION = gql`
  mutation AcceptProfessionalLinkInvitationMutation(
    $input: AcceptProfessionalLinkInvitationMutationInput!
  ) {
    acceptProfessionalLinkInvitationMutation(input: $input) {
      errors {
        field
        messages
      }
    }
  }
`;

export const ACCEPT_RESULTS_ACCESS_REQUEST_MUTATION = gql`
  mutation AcceptResultsAccessRequestMutation($input: AcceptResultsAccessRequestMutationInput!) {
    acceptResultsAccessRequestMutation(input: $input) {
      errors {
        field
        messages
      }
    }
  }
`;

export const PATIENT_PROFILE_MUTATION = gql`
  mutation PatientProfileMutation($input: PatientProfileMutationInput!) {
    patientProfileMutation(input: $input) {
      errors {
        field
        messages
      }
    }
  }
`;

export const GOALS_MUTATION = gql`
  mutation GoalsMutation($input: GoalsMutationInput!) {
    goalsMutation(input: $input) {
      patientProfile {
        id
        pk
        goals {
          id
          text
        }
      }
      errors {
        field
        messages
      }
    }
  }
`;

export const DIET_PREFERENCES_MUTATION = gql`
  mutation DietPreferencesMutation($input: DietPreferencesMutationInput!) {
    dietPreferencesMutation(input: $input) {
      errors {
        field
        messages
      }
    }
  }
`;

export const CREATE_PRACTITIONER_PROFILE_MUTATION = gql`
  mutation CreatePractitionerProfileMutation($input: CreatePractitionerProfileMutationInput!) {
    createPractitionerProfileMutation(input: $input) {
      errors {
        field
        messages
      }
    }
  }
`;

export const CREATE_STUDENT_PRACTITIONER_MUTATION = gql`
  mutation CreateStudentPractitionerMutation($input: CreateStudentPractitionerMutationInput!) {
    createStudentPractitionerMutation(input: $input) {
      errors {
        field
        messages
      }
    }
  }
`;

export const CREATE_PARTNER_INVITATION_MUTATION = gql`
  mutation CreatePartnerInvitationMutation($input: CreatePartnerInvitationInput!) {
    createPartnerInvitationMutation(input: $input) {
      createdAffiliateLink
      createdResultsAccessLink
      errors {
        field
        messages
      }
    }
  }
`;

export const PARTNER_LINK_CODE_QUERY = gql`
  query PartnerShareLinkRedirectPageQuery($partnerLinkCode: String!) {
    checkIncomingPartnerLinkCode(partnerLinkCode: $partnerLinkCode) {
      hashId
      identifier
      type
      tier
    }
  }
`;

export const ACKNOWLEDGE_NOTIFICATION_MUTATION = gql`
  mutation AcknowledgeNotificationMutation($input: AcknowledgeNotificationMutationInput!) {
    acknowledgeNotificationMutation(input: $input) {
      ok
    }
  }
`;

export const GET_NOTIFICATIONS = gql`
  query GetNotifications {
    notifications {
      id
      args
    }
  }
`;

export const CREATE_OR_EDIT_EXPERT_PROFILE_MUTATION = gql`
  mutation CreateOrEditExpertProfileMutation($input: CreateOrEditExpertProfileMutationInput!) {
    createOrEditExpertProfileMutation(input: $input) {
      expert {
        ...ExpertFields
      }
      errors {
        field
        messages
      }
    }
  }
  ${EXPERT_FIELDS}
`;

export const GENERATE_PRESIGNED_URL_FOR_USER_UPLOAD_MUTATION = gql`
  mutation GeneratePresignedUrlForUserUploadMutation($fileName: String, $public: Boolean) {
    generatePresignedUrlForUserUploadMutation(fileName: $fileName, public: $public) {
      fields
      url
      fileUrl
    }
  }
`;

export const SAVE_REVIEW = gql`
  mutation SaveReview($input: SaveReviewMutationInput!) {
    saveReviewMutation(input: $input) {
      errors {
        field
        messages
      }
    }
  }
`;

export const CONTACT_EXPERT_FIELDS = gql`
  fragment ContactExpertFields on ContactExpertType {
    id
    firstName
    lastName
    email
    phoneNumber
    message
    preferredMethodOfContact
    consultationType
    created
    responded
  }
`;

export const CALLBACK_REQUESTS_QUERY = gql`
  query CallbackRequestsQuery($page: Int, $orderBy: String, $search: String) {
    contactExpertsPagination(page: $page, orderBy: $orderBy, search: $search) {
      items {
        ...ContactExpertFields
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        totalPages
        currentPage
      }
    }
  }
  ${CONTACT_EXPERT_FIELDS}
`;

export const TOGGLE_RESPONDED_TO_CALLBACK_REQUEST_MUTATION = gql`
  mutation ToggleRespondedToCallbackRequestMutation($id: ID!) {
    toggleRespondedToCallbackRequestMutation(id: $id) {
      contactExpert {
        ...ContactExpertFields
      }
    }
  }
  ${CONTACT_EXPERT_FIELDS}
`;

export const TOGGLE_TEST_PRODUCT_FAVOURITE_MUTATION = gql`
  mutation ToggleTestProductFavouriteMutation($testProductId: ID!) {
    toggleTestProductFavouriteMutation(testProductId: $testProductId) {
      user {
        id
        patientProfile {
          ...PatientProfileFields
        }
      }
    }
  }
  ${PATIENT_PROFILE_FIELDS}
`;

export const ARCHIVE_CALLBACK_REQUEST_MUTATION = gql`
  mutation ArchiveCallbackRequestMutation($id: ID!) {
    archiveContactExpertMutation(id: $id) {
      ok
    }
  }
`;

export const LANGUAGE_FIELDS = gql`
  fragment LanguageFields on LanguageType {
    id
    name
  }
`;

export const LANGUAGES_QUERY = gql`
  query LanguageQuery {
    languages {
      ...LanguageFields
    }
  }
  ${LANGUAGE_FIELDS}
`;

export const SAVE_YOUR_DETAILS_MUTATION = gql`
  mutation SaveYourDetailsMutation($input: SaveYourDetailsMutationInput!) {
    saveYourDetailsMutation(input: $input) {
      practitionerProfile {
        id
        user {
          id
          firstName
          lastName
        }
        jobRole
        phoneNumber
      }
      errors {
        field
        messages
      }
    }
  }
`;

export const SAVE_PATIENT_PROFILE_MUTATION = gql`
  mutation SavePatientProfileMutation($input: SavePatientProfileMutationInput!) {
    savePatientProfileMutation(input: $input) {
      patientProfile {
        id
        dateOfBirth
        birthControl
        postMenopausal
        hrt
        height
        weight
        dateOfBirth
        gender
        user {
          id
          email
          firstName
          lastName
          isUserActive
        }
        guardian {
          id
          user {
            id
            firstName
            lastName
            email
          }
        }
      }
      errors {
        field
        messages
      }
    }
  }
`;

export const UPGRADE_STUDENT_PRACTITIONER_LEVEL_MUTATION = gql`
  mutation UpgradePractitionerLevelMutation($input: UpgradePractitionerLevelMutationInput!) {
    upgradePractitionerLevelMutation(input: $input) {
      errors {
        field
        messages
      }
    }
  }
`;

export const ANSWER_DUTCH_TEST_RELATED_QUESTIONS_MUTATION = gql`
  mutation AnswerDutchTestRelatedQuestionsMutation(
    $input: AnswerDutchTestRelatedQuestionsMutationInput!
  ) {
    answerDutchTestRelatedQuestionsMutation(input: $input) {
      patientProfile {
        id
        pk
        birthControl
        postMenopausal
        hrt
        user {
          id
        }
      }
      errors {
        field
        messages
      }
    }
  }
`;
